/**
 * `/admin/ai-ledger` — ☰ Admin › Ledger: what each user's Summaries cost.
 *
 * `docs/features/summary.md` § "Every charge is a ledger row, per user". Admin
 * only, read-only. `days` omitted is every row.
 *
 * Reads throw, as `api/activity.ts`'s do: an admin looking at an empty ledger
 * must be able to tell "nobody was charged" from "the server did not answer".
 */

import { currentUserId, requestHeaders } from "../lib/user";
import { httpBase } from "./backend";

const BASE = httpBase(import.meta.env.VITE_QV_SET_URL as string | undefined);

export interface LedgerUser {
  userKey: string;
  email: string | null;
  charges: number;
  billedUsd: number;
  listCostUsd: number;
  inputTokens: number;
  outputTokens: number;
  lastAt: string | null;
}

export interface LedgerCharge {
  at: string;
  /** `summary` (the shared note) or `summary-personal`. */
  reason: string;
  /** The symbol. */
  ref: string | null;
  model: string | null;
  billedUsd: number;
  listCostUsd: number | null;
  inputTokens: number;
  outputTokens: number;
}

async function read<T>(path: string): Promise<T> {
  if (!BASE) throw new Error("no server configured");
  const sep = path.includes("?") ? "&" : "?";
  const response = await fetch(`${BASE}${path}${sep}userId=${encodeURIComponent(currentUserId())}`, {
    headers: await requestHeaders(),
  });
  if (!response.ok) throw new Error((await response.text()) || `GET ${path} failed (${response.status})`);
  return (await response.json()) as T;
}

function period(days: number | null): string {
  return days === null ? "" : `?days=${days}`;
}

export async function fetchLedgerUsers(days: number | null): Promise<LedgerUser[]> {
  return (await read<{ users: LedgerUser[] }>(`/admin/ai-ledger${period(days)}`)).users;
}

export async function fetchLedgerCharges(userKey: string, days: number | null): Promise<LedgerCharge[]> {
  return (
    await read<{ charges: LedgerCharge[] }>(`/admin/ai-ledger/${encodeURIComponent(userKey)}${period(days)}`)
  ).charges;
}
